import type { ReactNode } from 'react';
import { useRef } from 'react';

import { useFormContext } from '#/components/Form/FormContext';
import { identity } from '#/utils/functional/function';

import type { FieldInternalProps } from './FieldInternal';
import { FieldInternal } from './FieldInternal';

type Props<TValues extends Record<string, unknown>> = {
  className?: string;
  name: string;
  nameOverride?: string;
  label?: ReactNode;
  errorRef?: React.RefObject<HTMLDivElement | null>;
  hideErrorLabel?: boolean;
  inlineLabel?: boolean;
  // TODO(@paprikka): see if we can make this exclusive to inlineLabel
  compactLabel?: boolean;
  hidden?: boolean;
  description?: ReactNode;
  renderInput: FieldInternalProps<TValues>['renderInput'];
  mapError?: FieldInternalProps<TValues>['mapError'];
  matchGeneralError?: string;
  inputProps?: FieldInternalProps<TValues>['inputProps'];
  customCompare?: FieldInternalProps<TValues>['customCompare'];
  customErrorMatch?: FieldInternalProps<TValues>['customErrorMatch'];
  noMargin?: boolean;
  noWrap?: boolean;
  success?: boolean;
  customError?: string | null | undefined;
};

export function Field<
  // oxlint-disable-next-line typescript/no-explicit-any
  TValues extends Record<string, any> = Record<string, any>,
>({
  className,
  name,
  nameOverride,
  label,
  errorRef,
  hideErrorLabel = false,
  inlineLabel,
  compactLabel,
  description,
  renderInput,
  mapError = identity,
  matchGeneralError,
  inputProps = {},
  customCompare,
  customErrorMatch,
  hidden,
  noMargin,
  noWrap,
  success,
  customError,
}: Props<TValues>) {
  const labelRef = useRef<HTMLLabelElement>(null);

  const {
    values,
    errors,
    getValue,
    updateValue,
    updateValueWith,
    fieldQaIdPrefix,
  } = useFormContext<TValues>();

  return (
    <FieldInternal
      className={className}
      values={values}
      errors={errors}
      getValue={getValue}
      // @ts-expect-error TS(2322): Type '<TKey extends keyof TValues>(name: TKey, val... Remove this comment to see the full error message
      updateValue={updateValue}
      updateValueWith={updateValueWith}
      fieldQaIdPrefix={fieldQaIdPrefix ?? ''}
      inlineLabel={inlineLabel}
      compactLabel={compactLabel}
      hideErrorLabel={hideErrorLabel}
      mapError={mapError}
      labelRef={labelRef}
      errorRef={errorRef}
      // @ts-expect-error TS(2322): Type '(params: { name: string; fieldQaIdPrefix: st... Remove this comment to see the full error message
      renderInput={renderInput}
      name={name}
      nameOverride={nameOverride}
      label={label}
      description={description}
      matchGeneralError={matchGeneralError}
      inputProps={inputProps}
      // @ts-expect-error TS(2322): Type '((previousProps: FieldInternalProps<TValues>... Remove this comment to see the full error message
      customCompare={customCompare}
      customErrorMatch={customErrorMatch}
      hidden={hidden}
      noMargin={noMargin}
      noWrap={noWrap}
      customError={customError}
      success={success}
    />
  );
}
